import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';
import { 
  Lightbulb, 
  PenTool, 
  GitBranch, 
  CreditCard, 
  Share2,
  ChevronRight,
  ExternalLink,
  Search,
  Building
} from 'lucide-react';

// Import all article components
import GettingStartedArticle from '../components/help/GettingStartedArticle';
import CreatingCustomizingViewsArticle from '../components/help/CreatingCustomizingViewsArticle';
import BuildingInteractiveFlowsArticle from '../components/help/BuildingInteractiveFlowsArticle';
import MaximizingAccessPassArticle from '../components/help/MaximizingAccessPassArticle';
import ExportingSharingDesignsArticle from '../components/help/ExportingSharingDesignsArticle';
import EnterpriseGuideArticle from '../components/help/EnterpriseGuideArticle';

const PublicHelpCenter = () => {
  const [activeArticle, setActiveArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const articles = [
    {
      id: 'getting-started',
      title: 'Getting Started with Prototyping',
      description: 'Learn how to set up your first project, understand the interface, and create your first prototype.',
      icon: Lightbulb,
      gradient: 'from-yellow-50 to-orange-50',
      iconColor: 'text-yellow-500',
      component: GettingStartedArticle,
      keywords: ['setup', 'first project', 'interface', 'prototype', 'beginner', 'getting started']
    },
    {
      id: 'creating-views',
      title: 'Creating and Customizing Views',
      description: 'Master the process of describing your UI, answering clarification questions, and refining generated views.',
      icon: PenTool,
      gradient: 'from-blue-50 to-indigo-50',
      iconColor: 'text-blue-500',
      component: CreatingCustomizingViewsArticle,
      keywords: ['customize', 'views', 'UI', 'design', 'refine', 'revision', 'create']
    },
    {
      id: 'building-flows',
      title: 'Building Interactive Flows',
      description: 'Combine multiple views into cohesive user journeys to simulate realistic interactions and test user flows.',
      icon: GitBranch,
      gradient: 'from-green-50 to-teal-50',
      iconColor: 'text-green-500',
      component: BuildingInteractiveFlowsArticle,
      keywords: ['flow', 'interactive', 'journey', 'navigation', 'sequence', 'user flow']
    },
    {
      id: 'maximizing-access',
      title: 'Maximizing Your Access Pass',
      description: 'Understand payment options and strategies to get the most value from your subscription.',
      icon: CreditCard,
      gradient: 'from-purple-50 to-violet-50',
      iconColor: 'text-purple-500',
      component: MaximizingAccessPassArticle,
      keywords: ['payment', 'subscription', 'access pass', 'billing', 'value', 'trial']
    },
    {
      id: 'exporting-sharing',
      title: 'Exporting and Sharing Your Designs',
      description: 'Learn to export prototypes, create shareable links, and integrate designs into your development workflow.',
      icon: Share2,
      gradient: 'from-red-50 to-pink-50',
      iconColor: 'text-red-500',
      component: ExportingSharingDesignsArticle,
      keywords: ['export', 'share', 'html', 'code', 'links', 'developers', 'collaborate']
    },
    {
      id: 'enterprise-guide',
      title: 'Enterprise Plan Guide',
      description: 'Set up and manage team access with enterprise accounts, including admin features, member management, and best practices.',
      icon: Building,
      gradient: 'from-blue-50 to-cyan-50',
      iconColor: 'text-blue-500',
      component: EnterpriseGuideArticle
    }
  ];

  // Filter articles based on search query
  const filteredArticles = searchQuery
    ? articles.filter(article => 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.keywords.some(keyword => keyword.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : articles;

  // If an article is selected, render that article
  if (activeArticle) {
    const ArticleComponent = activeArticle.component;
    return <ArticleComponent onBack={() => setActiveArticle(null)} />;
  }

  // Otherwise, render the help center main page
  return (
    <div className="font-sans antialiased text-gray-900">
      {/* Use MainNavbar with help as the active page */}
      <MainNavbar activePage="help" />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-6">Help Center</h1>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Find guides and resources to help you create, customize, and share your interactive prototypes.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-md mx-auto relative">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for help topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-3 pr-10 rounded-lg text-gray-800 shadow-md focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
              <Search className="absolute right-3 top-3 text-gray-400" size={20} />
            </div>
          </div>
        </div>
      </section>
      
      {/* Main Content Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-8">
              <h2 className="text-2xl font-bold mb-4">No results found</h2>
              <p className="text-gray-600 mb-6">We couldn't find any articles matching your search query.</p>
              <button 
                onClick={() => setSearchQuery('')}
                className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800"
              >
                Clear search
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredArticles.map((article) => (
                <div 
                  key={article.id}
                  className={`bg-gradient-to-r ${article.gradient} p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
                  onClick={() => setActiveArticle(article)}
                >
                  <div className="flex items-start">
                    <article.icon className={`w-6 h-6 ${article.iconColor} mr-3 flex-shrink-0 mt-1`} />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <h2 className="text-xl font-bold mb-2">{article.title}</h2>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-gray-700">{article.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Help Center Sections */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <ExternalLink className="w-5 h-5 mr-2 text-gray-700" />
                Additional Resources
              </h2>
              <ul className="space-y-3">
                <li>
                  <Link to="/demo" className="text-orange-500 hover:text-orange-600 flex items-center">
                    <ChevronRight className="w-4 h-4 mr-1" />
                    <span>View Demo</span>
                  </Link>
                </li>
                <li>
                  <Link to="/use-cases/designers" className="text-orange-500 hover:text-orange-600 flex items-center">
                    <ChevronRight className="w-4 h-4 mr-1" />
                    <span>Designed for Designers</span>
                  </Link>
                </li>
                <li>
                  <Link to="/features" className="text-orange-500 hover:text-orange-600 flex items-center">
                    <ChevronRight className="w-4 h-4 mr-1" />
                    <span>Feature Overview</span>
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="text-orange-500 hover:text-orange-600 flex items-center">
                    <ChevronRight className="w-4 h-4 mr-1" />
                    <span>Pricing Plans</span>
                  </Link>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-bold mb-4">Get Personal Support</h2>
              <p className="text-gray-700 mb-4">
                Can't find what you're looking for? Our support team is ready to assist you with any questions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-block px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 text-center"
                >
                  Contact Support
                </Link>
                <Link
                  to="/signup"
                  className="inline-block px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-center"
                >
                  Try Dessino.ai Free
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            <div className="bg-white p-5 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-2">How do I get started with Dessino.ai?</h3>
              <p className="text-gray-600">
                Sign up for a free account to get started with 10 free generations. After logging in, you can create your first project, choose style preferences, and start generating prototypes by describing what you want.
              </p>
            </div>

            <div className="bg-white p-5 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-2">Do I need design experience to use Dessino.ai?</h3>
              <p className="text-gray-600">
                No design experience is needed. Simply describe what you want in plain language, and our AI will generate professional designs based on your instructions.
              </p>
            </div>

            <div className="bg-white p-5 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-2">How does the payment system work?</h3>
              <p className="text-gray-600">
                We offer a pay-as-you-go model with no subscriptions or auto-renewals. You purchase access for a specific time period (30, 60, 90, 180, or 360 days) and during this period, you have unlimited access to all features.
              </p>
            </div>

            <div className="bg-white p-5 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold mb-2">Can I share my prototypes with others?</h3>
              <p className="text-gray-600">
                Yes, you can create shareable links for both individual views and flows. These links can be password-protected and set with expiration dates for secure sharing with team members and stakeholders.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Start Creating?</h2>
          <p className="text-xl mb-8">
            Begin with 10 free generations. No credit card required.
          </p>
          <Link to="/signup" className="px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-block">
            Start Free Trial
          </Link>
        </div>
      </section>
      
      {/* Footer */}
      <MainFooter />
    </div>
  );
};

export default PublicHelpCenter;